/**
 * IdeaCanvasChatService
 *
 * Wraps @google/genai SDK for multi-turn chat and image generation
 * in the Idea Canvas feature. Text chat uses streaming for progressive display.
 *
 * When the backend proxy is available (user is authenticated, no personal key),
 * all calls are routed through it so the API key never reaches the browser.
 */

import { GoogleGenAI } from '@google/genai';
import type { IdeaChatMessage, IdeaChatModelId, IdeaChatMode } from '../../../core/types/slot-screen-generator.types';
import { useSlotAssetGeneratorStore } from '@store/slotAssetGeneratorStore';
import { resolveApiKey } from '@utils/api-keys';
import { shouldUseProxy, proxyGenerateContent, proxyGenerateContentStream } from '../ai-proxy';

export interface ChatResponse {
  text: string;
  images: string[];          // blob URLs for generated images
  thoughtSignature?: string; // to echo back in next turn
  thoughtText?: string;      // captured thinking content
}

/**
 * Convert a blob URL to base64 data + mimeType.
 */
async function blobUrlToBase64(url: string): Promise<{ data: string; mimeType: string } | null> {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const b of bytes) binary += String.fromCharCode(b);
    return { data: btoa(binary), mimeType: blob.type || 'image/png' };
  } catch {
    return null;
  }
}

/**
 * Build the multi-turn contents array from history + current user message.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function buildContents(history: IdeaChatMessage[], userMessage: IdeaChatMessage): Promise<any[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contents: any[] = [];

  for (const msg of history) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parts: any[] = [];

    if (msg.role === 'model' && msg.thoughtSignature) {
      parts.push({ text: '', thoughtSignature: msg.thoughtSignature });
    }

    if (msg.text) {
      parts.push({ text: msg.text });
    }

    if (msg.role === 'user' && msg.images.length > 0) {
      for (const imgUrl of msg.images) {
        if (!imgUrl) continue;
        const base64 = await blobUrlToBase64(imgUrl);
        if (base64) {
          parts.push({ inlineData: { data: base64.data, mimeType: base64.mimeType } });
        }
      }
    }

    if (parts.length > 0) {
      contents.push({ role: msg.role === 'user' ? 'user' : 'model', parts });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userParts: any[] = [];
  if (userMessage.text) userParts.push({ text: userMessage.text });
  for (const imgUrl of userMessage.images) {
    if (!imgUrl) continue;
    const base64 = await blobUrlToBase64(imgUrl);
    if (base64) {
      userParts.push({ inlineData: { data: base64.data, mimeType: base64.mimeType } });
    }
  }
  contents.push({ role: 'user', parts: userParts });

  return contents;
}

function getApiKeyAndClient(): { ai: GoogleGenAI; apiKey: string } {
  const apiKeys = useSlotAssetGeneratorStore.getState().apiKeys;
  const apiKey = resolveApiKey('google', apiKeys.google);
  if (!apiKey) {
    throw new Error('Google API key required. Set GOOGLE_API_KEY or add key in Slot Asset Generator settings.');
  }
  return { ai: new GoogleGenAI({ apiKey }), apiKey };
}

/**
 * Process streaming chunk parts — extract text and thought signatures.
 * Shared logic used by both proxy SSE and direct SDK streaming paths.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function processStreamChunk(chunk: any, result: ChatResponse, onTextChunk: (delta: string) => void, onThoughtChunk: (delta: string) => void) {
  const candidate = chunk.candidates?.[0];
  if (!candidate?.content?.parts) return;

  for (const part of candidate.content.parts) {
    if (part.thoughtSignature && !part.thought) {
      result.thoughtSignature = part.thoughtSignature as string;
    }
    if (part.thought && part.thoughtSignature) {
      result.thoughtSignature = part.thoughtSignature as string;
      if (part.text) {
        result.thoughtText = (result.thoughtText ?? '') + part.text;
        onThoughtChunk(part.text);
      }
      continue;
    }
    if (part.text && !part.thought) {
      result.text += part.text;
      onTextChunk(part.text);
    }
  }
}

/**
 * Send a streaming chat message. Calls onTextChunk for each text delta.
 * Returns the final complete response (with images and thoughtSignature).
 *
 * For image generation mode, falls back to non-streaming (images arrive all at once).
 */
export async function sendChatMessageStream(
  history: IdeaChatMessage[],
  userMessage: IdeaChatMessage,
  modelId: IdeaChatModelId,
  mode: IdeaChatMode,
  onTextChunk: (delta: string) => void,
  onThoughtChunk?: (delta: string) => void,
  imageCount?: number,
): Promise<ChatResponse> {
  const contents = await buildContents(history, userMessage);
  const useProxy = shouldUseProxy();

  const isImageMode = mode === 'create-image';
  const actualModel = isImageMode ? 'gemini-3.1-flash-image-preview' : modelId;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const config: Record<string, any> = {};
  if (isImageMode) {
    config.responseModalities = ['TEXT', 'IMAGE'];
  } else {
    config.thinkingConfig = {
      includeThoughts: true,
      thinkingBudget: 8192,
    };
  }

  // ── Image mode: non-streaming (images can't stream) ──
  if (isImageMode) {
    const makeRequest = async () => {
      if (useProxy) {
        const data = await proxyGenerateContent({ model: actualModel, contents, config });
        return parseFullResponse(data);
      } else {
        const { ai } = getApiKeyAndClient();
        const response = await ai.models.generateContent({ model: actualModel, contents, config });
        return parseFullResponse(response);
      }
    };

    const count = imageCount ?? 1;
    if (count <= 1) {
      return makeRequest();
    }

    // Fire N parallel requests, merge all images + text
    const results = await Promise.all(Array.from({ length: count }, () => makeRequest()));
    const merged: ChatResponse = { text: '', images: [] };
    for (const r of results) {
      if (r.text && !merged.text) merged.text = r.text;
      merged.images.push(...r.images);
      if (r.thoughtSignature) merged.thoughtSignature = r.thoughtSignature;
    }
    return merged;
  }

  // ── Text chat mode: use streaming ──
  const result: ChatResponse = { text: '', images: [] };
  const thoughtCb = onThoughtChunk ?? (() => {});

  if (useProxy) {
    // Stream via backend proxy (SSE)
    await proxyGenerateContentStream(
      { model: actualModel, contents, config },
      (chunk) => processStreamChunk(chunk, result, onTextChunk, thoughtCb),
    );
  } else {
    // Direct SDK streaming (fallback with user's own key)
    const { ai } = getApiKeyAndClient();
    const stream = await ai.models.generateContentStream({
      model: actualModel,
      contents,
      config,
    });

    for await (const chunk of stream) {
      processStreamChunk(chunk, result, onTextChunk, thoughtCb);
    }
  }

  return result;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseFullResponse(response: any): ChatResponse {
  const result: ChatResponse = { text: '', images: [] };
  const candidate = response.candidates?.[0];
  if (!candidate?.content?.parts) return result;

  for (const part of candidate.content.parts) {
    if (part.thought && part.thoughtSignature) {
      result.thoughtSignature = part.thoughtSignature as string;
      if (part.text) {
        result.thoughtText = (result.thoughtText ?? '') + part.text;
      }
      continue;
    }
    if (part.thoughtSignature && !part.thought) {
      result.thoughtSignature = part.thoughtSignature as string;
    }
    if (part.text && !part.thought) {
      result.text += part.text;
    }
    if (part.inlineData?.data && part.inlineData.mimeType?.startsWith('image/')) {
      const bytes = Uint8Array.from(atob(part.inlineData.data), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: part.inlineData.mimeType });
      result.images.push(URL.createObjectURL(blob));
    }
  }

  return result;
}

/**
 * Non-streaming version (kept for backwards compatibility).
 */
export async function sendChatMessage(
  history: IdeaChatMessage[],
  userMessage: IdeaChatMessage,
  modelId: IdeaChatModelId,
  mode: IdeaChatMode,
): Promise<ChatResponse> {
  return sendChatMessageStream(history, userMessage, modelId, mode, () => {});
}
